<template>
  <div class="pagination">
    <NuxtLink
      v-if="root"
      aria-label="prev"
      class="prev"
      :class="{ disabled: firstPageIsSelected }"
      :to="value === 1 ? to(1) : to(value - 1)"
    >
      <UiComponentIcon type="chevron-left" />
    </NuxtLink>
    <div
      v-else
      class="prev"
      :class="{ disabled: firstPageIsSelected }"
      @click="prev"
    >
      <UiComponentIcon type="chevron-left" />
    </div>
    <div class="navigation">
      <AtomButton
        :to="to(1)"
        :type="firstPageIsSelected ? 'secondary' : 'text'"
        @click="goTo(1)"
      >
        1
      </AtomButton>
      <AtomButton
        v-if="displayablePages[0] > 2"
        class="break"
        type="text"
      >
        •••
      </AtomButton>
      <AtomButton
        v-for="page in displayablePages"
        :key="page"
        :to="to(page)"
        :type="page === selected ? 'secondary' : 'text'"
        @click="goTo(page)"
      >
        {{ page }}
      </AtomButton>
      <AtomButton
        v-if="displayablePages[0] < count - range"
        class="break"
        type="text"
      >
        •••
      </AtomButton>
      <AtomButton
        v-if="count > 1"
        :to="to(count)"
        :type="lastPageIsSelected ? 'secondary' : 'text'"
        @click="goTo(count)"
      >
        {{ count }}
      </AtomButton>
    </div>
    <NuxtLink
      v-if="root"
      aria-label="next"
      class="next"
      :class="{ disabled: lastPageIsSelected }"
      :to="value === count ? to(count) : to(value + 1)"
    >
      <UiComponentIcon type="chevron-right" />
    </NuxtLink>
    <div
      v-else
      class="next"
      :class="{ disabled: lastPageIsSelected }"
      @click="next"
    >
      <UiComponentIcon type="chevron-right" />
    </div>
  </div>
</template>

<script setup>
// Emitter
const emit = defineEmits([
  'pagination:change',
])

// Props
const props = defineProps({
  count: {
    required: true,
    type: Number,
    validator(value) {
      return value >= 1
    },
  },
  folderName: {
    default() {
      return 'page'
    },
    required: false,
    type: String,
  },
  range: {
    default() {
      return 2
    },
    required: false,
    type: Number,
  },
  root: {
    default() {
      return undefined
    },
    required: false,
    type: String,
  },
  value: {
    default() {
      return 1
    },
    required: false,
    type: Number,
    validator(value) {
      return value >= 1
    },
  },
})

// Refs
const selected = ref(1)

// Computed Properties
const displayablePages = computed(() => {
  const pages = new Array(props.count).fill(0).map((_, index) => index + 1)
  const displayablePages = []
  switch (true) {
    case selected.value === 1:
      displayablePages.push(
        ...pages.slice(selected.value, selected.value + props.range),
      )
      break

    case selected.value === 2:
      displayablePages.push(
        ...pages.slice(selected.value - 1, selected.value + props.range - 1),
      )
      break

    case selected.value === props.count - 1:
      displayablePages.push(
        ...pages.slice(selected.value - props.range, selected.value),
      )
      break

    case selected.value === props.count:
      displayablePages.push(...pages.slice(-props.range - 1, -1))
      break

    default:
      displayablePages.push(
        ...pages.slice(selected.value - props.range + 1, selected.value + 1),
      )
  }

  return displayablePages.filter(
    page => page !== 1 && page !== props.count,
  )
})

const firstPageIsSelected = computed(() => {
  return selected.value === 1
})

const lastPageIsSelected = computed(() => {
  return selected.value === props.count
})

// Methods
function goTo(page) {
  if (selected.value === page || props.root) {
    return
  }
  selected.value = page
  emit('pagination:change', selected.value)
}

function next() {
  if (selected.value === props.count || props.root) {
    return
  }
  selected.value = selected.value + 1
  emit('pagination:change', selected.value)
}

function prev() {
  if (selected.value === 1 || props.root) {
    return
  }
  selected.value = selected.value - 1
  emit('pagination:change', selected.value)
}

function reset() {
  selected.value = 1
}

function to(page) {
  if (!props.root) {
    return undefined
  }
  return page === 1 ? props.root : `${props.root}/${props.folderName}/${page}`
}

// Lifecycle Hooks
onBeforeMount(() => {
  selected.value = props.value
})

// Expose
defineExpose({
  reset,
})
</script>

<style lang="postcss" scoped>
.pagination {
  @apply flex flex-row w-full justify-center items-center;

  .prev,
  .next {
    @apply cursor-pointer;

    i {
      @apply font-semibold text-winter-green-900;
    }

    &.disabled {
      @apply cursor-default;

      i {
        @apply text-grey-blue-200;
      }
    }
  }

  .navigation {
    @apply flex flex-row mx-5;

    @screen md {
      @apply mx-6;
    }

    .button {
      @apply w-10 h-10 mx-2 px-0 font-medium text-winter-green-900;

      @screen md {
        margin: 0 10px;
      }

      &:first-child {
        @apply ml-0;
      }

      &:last-child {
        @apply mr-0;
      }

      &.secondary {
        @apply bg-livestorm-blue-100 text-livestorm-blue-700 cursor-default;
      }

      &.break {
        @apply cursor-default;
      }
    }
  }
}
</style>
